import { Link, router } from '@inertiajs/react'
import { ArrowLeft, ArrowRight, LogOut, MessageSquare, BarChart3 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface Stats {
  total_inquiries: number
  recent_inquiries: number
  total_visits: number
  recent_visits: number
}

function NavCard({
  href,
  icon: Icon,
  title,
  description,
  total,
  recent,
}: {
  href: string
  icon: React.ElementType
  title: string
  description: string
  total: number
  recent: number
}) {
  return (
    <Link href={href} className="block group">
      <Card className="border-brand-light shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-brand-red/10 p-3">
                <Icon className="h-6 w-6 text-brand-red" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-brand-dark group-hover:text-brand-red transition-colors">
                  {title}
                </h2>
                <p className="text-sm text-brand-gray mt-1">{description}</p>
                <div className="flex items-center gap-4 mt-4">
                  <div>
                    <p className="text-2xl font-bold text-brand-dark">{total.toLocaleString()}</p>
                    <p className="text-xs text-brand-gray">Total</p>
                  </div>
                  <div className="h-8 w-px bg-brand-light" />
                  <div>
                    <p className="text-2xl font-bold text-brand-dark">{recent.toLocaleString()}</p>
                    <p className="text-xs text-brand-gray">Last 7 days</p>
                  </div>
                </div>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-brand-red group-hover:translate-x-0.5 transition-all mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

export default function Home({ stats }: { stats: Stats }) {
  return (
    <div className="min-h-screen bg-brand-light font-montserrat text-brand-dark">
      <header className="bg-brand-dark py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <img
                src="/curbtek-logo.png"
                alt="CurbTek Logo"
                className="h-10 object-contain"
              />
            </div>
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Site
              </Link>
              <button
                onClick={() => router.delete('/session')}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Log Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-2xl font-bold text-brand-dark mb-8">Admin</h1>

        <div className="space-y-4">
          <NavCard
            href="/admin/inquiries"
            icon={MessageSquare}
            title="Inquiries"
            description="Review contact form submissions"
            total={stats.total_inquiries}
            recent={stats.recent_inquiries}
          />
          <NavCard
            href="/admin/visits"
            icon={BarChart3}
            title="Analytics"
            description="Visits, events, and traffic sources"
            total={stats.total_visits}
            recent={stats.recent_visits}
          />
        </div>
      </main>
    </div>
  )
}
